import { DataStore } from "aws-amplify/datastore";
import { Invoice, ServiceRendered } from "../../models";
import { InvoiceStatus } from "../../models";
import { ReportRepository } from "../interfaces";
import { TaxYearSummary, ClientIncomeSummary, InvoiceRecord, ReportBasis } from "../types";
import { TaxYearConfig, calculateMileageAllowance } from "../../pages/Reports/taxYearConfig";
import { roundToPence } from "../../utils/money";

// paidAt is an AWSDate (date-only, "yyyy-MM-dd") — comparisons below rely on every paidAt value
// being date-only so they sort correctly against the date-only taxYear.start/end bounds.

function toInvoiceRecord(inv: InstanceType<typeof Invoice>): InvoiceRecord {
  return {
    id: inv.id,
    clientID: inv.clientID,
    invoiceName: inv.invoiceName,
    filePath: inv.filePath,
    startDate: inv.startDate,
    endDate: inv.endDate,
    subtotal: inv.subtotal,
    owner: inv.owner,
    label: inv.label,
    status: inv.status ?? null,
    paidAt: inv.paidAt ?? null,
    servicesRendered: [],
  };
}

export class DatastoreReportRepository implements ReportRepository {
  async getTaxYearSummary(taxYear: TaxYearConfig, basis: ReportBasis): Promise<TaxYearSummary> {
    const { start, end } = taxYear;

    const [srsInDateRange, sentInvoices] = await Promise.all([
      DataStore.query(ServiceRendered, (s) => s.and((s) => [s.date.ge(start), s.date.le(end)])),
      DataStore.query(Invoice, (i) =>
        i.and((i) => [i.status.eq(InvoiceStatus.SENT), i.endDate.ge(start), i.endDate.le(end)])
      ),
    ]);

    // Box 17 mileage allowance is a flat-rate deduction for miles actually driven, not a
    // "payment received" concept, so it is always computed from ServiceRendered.date and is
    // unaffected by the cash/accruals basis toggle.
    let totalMilesDriven = 0;
    for (const sr of srsInDateRange) {
      totalMilesDriven += sr.mileage ?? 0;
    }

    const outstandingInvoices: InvoiceRecord[] = sentInvoices.map(toInvoiceRecord);

    const activeSrs =
      basis === "cash"
        ? await this.getCashBasisSrs(taxYear)
        : await this.getAccrualsBasisSrs(taxYear, srsInDateRange);

    const clientMap = new Map<string, ClientIncomeSummary>();
    let totalServiceIncome = 0;
    let totalMileageBilled = 0;

    for (const sr of activeSrs) {
      const [client, service] = await Promise.all([sr.client, sr.service]);
      if (!client) continue;
      const cost = (sr.serviceCost ?? service?.cost ?? 0) * sr.quantity;
      // Round once at extraction so every downstream usage (totalMileageBilled and the
      // per-client breakdown) rounds each activity's mileage cost individually before it's
      // added into any total, rather than summing raw fractional values and rounding once.
      const mileageCost = roundToPence(sr.mileageCost ?? 0);

      totalServiceIncome += cost;
      totalMileageBilled += mileageCost;

      const existing = clientMap.get(client.id);
      if (existing) {
        existing.serviceIncome += cost;
        existing.mileageBilled += mileageCost;
      } else {
        clientMap.set(client.id, {
          clientId: client.id,
          clientName: `${client.name} ${client.surname}`.trim(),
          serviceIncome: cost,
          mileageBilled: mileageCost,
        });
      }
    }

    return {
      serviceIncome: totalServiceIncome,
      mileageBilled: totalMileageBilled,
      totalMilesDriven,
      mileageAllowance: calculateMileageAllowance(totalMilesDriven),
      perClient: Array.from(clientMap.values()),
      outstandingInvoices,
    };
  }

  private async getAccrualsBasisSrs(
    taxYear: TaxYearConfig,
    srsInDateRange: InstanceType<typeof ServiceRendered>[]
  ): Promise<InstanceType<typeof ServiceRendered>[]> {
    const { start, end } = taxYear;

    // Scoped to invoices whose period overlaps the tax year, since an SR linked to a
    // VOIDED/CANCELLED invoice will only fall in srsInDateRange (above) if its invoice's
    // period does too. Only queried for accruals basis — cash basis attributes income by
    // paidAt via getCashBasisSrs and never consults this list.
    const voidedInvoices = await DataStore.query(Invoice, (i) =>
      i.and((i) => [
        i.or((i) => [i.status.eq(InvoiceStatus.VOIDED), i.status.eq(InvoiceStatus.CANCELLED)]),
        i.startDate.le(end),
        i.endDate.ge(start),
      ])
    );

    const excludedIds = new Set(voidedInvoices.map((i) => i.id));
    return srsInDateRange.filter((sr) => !sr.invoiceID || !excludedIds.has(sr.invoiceID));
  }

  private async getCashBasisSrs(taxYear: TaxYearConfig): Promise<InstanceType<typeof ServiceRendered>[]> {
    const { start, end } = taxYear;

    // Unbounded scan of every ServiceRendered (and all PAID invoices): cash-basis attribution
    // depends on the linked invoice's paidAt, not the SR's own date, so SRs can't be
    // date-prefiltered the way the accruals path does. Grows with total table size, not with
    // the tax year's size — acceptable for current data volumes but worth revisiting if it shows
    // up as a performance issue.
    const [allSrs, paidInvoices] = await Promise.all([
      DataStore.query(ServiceRendered),
      DataStore.query(Invoice, (i) => i.status.eq(InvoiceStatus.PAID)),
    ]);

    // PAID invoices with a payment date in the tax year: their linked SRs count regardless of
    // the underlying ServiceRendered.date, since cash basis attributes income by paidAt. Every
    // PAID invoice is expected to have paidAt set by the time this report is enabled — see the
    // backfill migration run ahead of the feature flag — so invoices without one are excluded
    // rather than falling back to SR.date.
    const paidWithDateInRange = paidInvoices.filter((inv) => inv.paidAt && inv.paidAt >= start && inv.paidAt <= end);
    const paidIdsInRange = new Set(paidWithDateInRange.map((inv) => inv.id));

    // Un-invoiced SRs are never cash-basis income — they can't have been paid without an invoice.
    return allSrs.filter((sr) => !!sr.invoiceID && paidIdsInRange.has(sr.invoiceID));
  }
}
